import type { Fact, FactCatalog, FactId, Operation, StrategyId } from "../model.ts";
import { factId } from "../ids.ts";

/** Semantic exposure metadata for our worked solutions, never extracted from learner answers. */
export function answerExposureFactIds(fact: Fact, strategy: StrategyId | null, catalog: FactCatalog): readonly FactId[] {
  const ids = new Set([fact.id]);
  const add = (operation: Operation, left: number, right: number) => {
    const answer = operation === "ADD" ? left + right : left - right;
    if (left < 0 || right < 0 || answer < 0 || left > 20 || right > 20 || answer > 20) return;
    const id = factId(operation, left, right, answer);
    if (catalog.factById.has(id)) ids.add(id);
  };
  const { left, right, answer, operation } = fact;
  switch (strategy) {
    case "DOUBLES": if (operation === "SUBTRACT") add("ADD", right, right); break;
    case "MAKE_10": if (operation === "SUBTRACT") add("ADD", right, answer); break;
    case "NEAR_DOUBLE": { const base = operation === "ADD" ? Math.min(left, right) : right; add("ADD", base, base); break; }
    case "COMMUTE": add("ADD", right, left); break;
    case "INVERSE_FACT": operation === "ADD" ? add("SUBTRACT", answer, right) : add("ADD", answer, right); break;
    case "TEEN_ANALOGY": {
      const teen = operation === "ADD" ? Math.max(left, right) : left;
      const small = operation === "ADD" ? Math.min(left, right) : right;
      add(operation, teen - 10, small); add("ADD", 10, answer - 10); break;
    }
    case "VIA_10_ADD": {
      const toTen = 10 - left, rest = right - toTen;
      add("ADD", left, toTen); add("ADD", 10, rest); add("ADD", toTen, rest); add("SUBTRACT", right, toTen); break;
    }
    case "BACK_VIA_10": {
      const toTen = left - 10, rest = right - toTen;
      add("SUBTRACT", left, toTen); add("SUBTRACT", 10, rest); add("ADD", toTen, rest); add("SUBTRACT", right, toTen); break;
    }
    case "COUNT_ON": case null: {
      if (operation === "ADD") {
        const start = Math.max(left, right), steps = Math.min(left, right);
        for (let i = 0; i < steps; i++) add("ADD", start + i, 1);
      }
      break;
    }
    case "COUNT_BACK": for (let i = 0; i < right; i++) add("SUBTRACT", left - i, 1); break;
  }
  return [...ids];
}
